"use client";

import { isMobile } from "@/utils/browser";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";
import { useSessionStorage } from "usehooks-ts";
import { Action, useButtonAnalytics } from "./AnalyticsProvider";

export type WidgetTab = "Send" | "Receive" | "Swap" | "Bridge" | "Buy";

export type OpenWidgetTabFrom = "RightNav" | "TokenDetailsHeader" | "Balances";

export interface OpenWidgetParams {
  tab: WidgetTab;
  from: OpenWidgetTabFrom;
  targetTokenAddress?: string;
}

interface RightNavContextType {
  isCollapsed: boolean;
  isInitialLoad: boolean;
  toggleCollapse: () => void;
  widgetConfig: OpenWidgetParams | null;
  openWidgetTab: (params: OpenWidgetParams) => void;
  closeWidgetTab: () => void;
  selectedWallet: string;
  setSelectedWallet: (wallet: string) => void;
  isWidgetOpen: boolean;
}

const COLLAPSED_BY_DEFAULT_PAGES = ["/balance"];
const RIGHT_NAV_STATE_KEY = "rightNavCollapsed";
const WIDGET_CONFIG_KEY = "rightNavWidgetConfig";

function getDefaultCollapsedState(pathname: string | null): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  if (isMobile()) {
    return true;
  }

  if (pathname) {
    return COLLAPSED_BY_DEFAULT_PAGES.some((page) => pathname.startsWith(page));
  }

  return false; // Open by default on desktop
}

function getSavedCollapsedState(pathname: string | null): boolean {
  // Check if we're on the client side before accessing localStorage
  if (typeof window === "undefined") {
    return getDefaultCollapsedState(pathname);
  }

  // Always collapse on mobile for game pages, regardless of pathname
  if (isMobile() && pathname?.startsWith("/games")) {
    return true;
  }

  // On desktop, force open for /games pages
  if (pathname?.startsWith("/games")) {
    return false;
  }

  // Check if there's a saved state in localStorage
  const savedState = localStorage.getItem(RIGHT_NAV_STATE_KEY);
  if (savedState !== null) {
    return JSON.parse(savedState);
  }

  // Fall back to default behavior if no saved state
  return getDefaultCollapsedState(pathname);
}

const RightNavContext = createContext<RightNavContextType | undefined>(
  undefined,
);

export function RightNavProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isCollapsed, setIsCollapsed] = useState(() =>
    getSavedCollapsedState(pathname),
  );
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [widgetConfig, setWidgetConfig] =
    useSessionStorage<OpenWidgetParams | null>(WIDGET_CONFIG_KEY, null);
  const [selectedWallet, setSelectedWallet] = useState<string>("");
  const { track } = useButtonAnalytics();

  useEffect(() => {
    // Set the correct collapsed state after hydration, using saved state if available
    setIsCollapsed(getSavedCollapsedState(pathname));
  }, [pathname]);

  useEffect(() => {
    if (!isCollapsed && !pathname?.startsWith("/games"))
      document.body.classList.add(...["overflow-hidden", "md:overflow-auto"]);
    else
      document.body.classList.remove(
        ...["overflow-hidden", "md:overflow-auto"],
      );
  }, [isCollapsed, pathname]);

  const toggleCollapse = () => {
    // Enable animations after first user interaction
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    if (typeof window !== "undefined") {
      localStorage.setItem(
        RIGHT_NAV_STATE_KEY,
        JSON.stringify(newCollapsedState),
      );
    }
  };

  const openWidgetTab = (params: OpenWidgetParams) => {
    // Enable animations after first user interaction
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }

    // Ensure nav is expanded when opening tab
    if (isCollapsed) {
      setIsCollapsed(false);
      // Save expanded state to localStorage
      if (typeof window !== "undefined") {
        localStorage.setItem(RIGHT_NAV_STATE_KEY, JSON.stringify(false));
      }
    }

    setWidgetConfig(params);
  };

  const closeWidgetTab = () => {
    setWidgetConfig(null);

    track({
      action: Action.PRESSED,
      extras: {
        id: "closeWidgetTab",
        type: "cta",
        label: "Close widget tab",
        section: "RightNav",
      },
    });
  };

  return (
    <RightNavContext.Provider
      value={{
        isCollapsed,
        isInitialLoad,
        toggleCollapse,
        widgetConfig,
        openWidgetTab,
        closeWidgetTab,
        selectedWallet,
        setSelectedWallet,
        isWidgetOpen: widgetConfig !== null,
      }}
    >
      {children}
    </RightNavContext.Provider>
  );
}

export function useRightNav() {
  const context = useContext(RightNavContext);
  if (context === undefined) {
    throw new Error("useRightNav must be used within a RightNavProvider");
  }
  return context;
}
